import { Vue, Component, Emit, Prop, Watch} from 'vue-property-decorator';
import Modal from '@/assets/common/Modal/Modal.vue'
import { toCamel } from "snake-camel";
import { Chemicals } from "@/types/Component";
import { ChemicalsResponse } from "@/types/APIResponse";
import Paginate from 'vuejs-paginate'

type Chemical = {
    id: number;
    name: string;
    unitName: string | null;
    warehouseId : string | null;
    warehouseName : string | null;
    lastUsed: Date | null;
    isExpired?: boolean;
}
type ChemicalList = {
    id: number;
    displayName: string;
}

type Sel = {
    id: number;
    warehouseId : string;
}

type ChemicalsGet = {
    recommendations: Chemical[];
    histories: Chemical[];
    chemicalsFromWarehouseMaster: Chemical[];
}

type Warehouse = {
    warehouseId: string;
    warehouseName: string;
}

type ChemicalSortTab = 'recommendation' | 'history' | 'warehouse-master'
type ChemicalSortField = 'id' | 'name' | 'lastUsed'

const createChemicalSortFields = (): Record<ChemicalSortTab, ChemicalSortField> => ({
    recommendation: 'id',
    history: 'id',
    'warehouse-master': 'id'
});

@Component({
    components: {
        Modal,
        Paginate
    }
})
export default class PesticideSelectionModal extends Vue {

    @Prop() 
    isShowPesticideSelectionModal!: boolean

    @Prop()
    cropItemId!: string;

    @Prop() 
    pdTenantId!: string

    @Prop() 
    sitemId!: string;

    @Prop()
    isWorkRecord!:boolean

    @Prop()
    isChemRecommendedOnly!: boolean

    @Prop()
    initialValue!: Sel[];
    
    searchType:'name' | 'id' = 'name';
    totalPages = 0;
    offset = 0;
    limit = 20;
    currentPage = 1;
    chemicals: Chemicals = {
        chemicals: [],
        hasMore: false,
        totalCount: 0
    }
    isChemicalMasterLoading = false;
    isSpinnerLoading = false;
    showAllChemicals = false;
    warehouses: Warehouse[] = [];
    selectedWarehouseId = null;
    recommendations: Chemical[] = [];
    filteredRecommendations: Chemical[] = [];
    histories: Chemical[] = [];
    filteredHistories: Chemical[] = [];
    chemicalsFromWarehouseMaster: Chemical[] = [];
    filteredChemicalsFromWarehouseMaster: Chemical[] = [];
    searchRecommendation = ''
    searchHistory = ''
    searchWarehouseMaster = ''
    searchChemicalMaster = ''
    selectedChemicals: Chemical[] = []
    checkedChemicals:any []= []
    selectedChemicalKeys: Set<string> = new Set();
    selectedTab: 'recommendation' | 'history' | 'warehouse-master' | 'chemical-master' = 'recommendation'
    chemicalMasterSortColumn: 'id' | 'display_type' | 'display_name' = 'id';
    chemicalMasterSortOrder: 'asc' | 'desc' = 'asc';
    sortOrders: Record<ChemicalSortTab, boolean> = {
        recommendation: true,
        history: true,
        'warehouse-master': true
    };
    sortFields: Record<ChemicalSortTab, ChemicalSortField> = createChemicalSortFields();
    
    selectTab(tab: 'recommendation' | 'history' | 'warehouse-master' | 'chemical-master'): void {
        this.selectedTab = tab
    }

    isSortActive(tab: ChemicalSortTab, fieldName: ChemicalSortField): boolean {
        return this.sortFields[tab] === fieldName;
    }

    isSortAscending(tab: ChemicalSortTab, fieldName: ChemicalSortField): boolean {
        return this.isSortActive(tab, fieldName) && this.sortOrders[tab];
    }

    isSortDescending(tab: ChemicalSortTab, fieldName: ChemicalSortField): boolean {
        return this.isSortActive(tab, fieldName) && !this.sortOrders[tab];
    }

    private getTargetList(tab: ChemicalSortTab): Chemical[] {
        return tab === 'recommendation'
            ? this.filteredRecommendations
            : tab === 'history'
                ? this.filteredHistories
                : this.filteredChemicalsFromWarehouseMaster;
    }

    private compareChemicals(a: Chemical, b: Chemical, fieldName: ChemicalSortField, isAscending: boolean): number {
            const aValue = a[fieldName];
            const bValue = b[fieldName];
            if (aValue === null && bValue === null) return 0;
            if (aValue === null) return 1;
            if (bValue === null) return -1;

            if (fieldName === 'lastUsed') {
                const aTime = new Date(aValue as Date).getTime();
                const bTime = new Date(bValue as Date).getTime();
                return isAscending ? aTime - bTime : bTime - aTime;
            }

            const aNum = Number(aValue);
            const bNum = Number(bValue);
            const isANum = !isNaN(aNum);
            const isBNum = !isNaN(bNum);
            if (isANum && isBNum) {
                return isAscending ? aNum - bNum : bNum - aNum;
            }

            return isAscending
                ? String(aValue).localeCompare(String(bValue), 'ja')
                : String(bValue).localeCompare(String(aValue), 'ja');
    }

    private applySort(tab: ChemicalSortTab): void {
        this.getTargetList(tab).sort((a, b) => this.compareChemicals(a, b, this.sortFields[tab], this.sortOrders[tab]));
    }

    sortChemicals(tab: ChemicalSortTab, fieldName: ChemicalSortField): void {
        if (this.sortFields[tab] === fieldName) {
            this.sortOrders[tab] = !this.sortOrders[tab];
        } else {
            this.sortFields[tab] = fieldName;
            this.sortOrders[tab] = true;
        }
        this.applySort(tab);
    }

    sortChemicalMaster(column: 'id' | 'display_type' | 'display_name'): void {
        if (this.chemicalMasterSortColumn === column) {
            this.chemicalMasterSortOrder = this.chemicalMasterSortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            this.chemicalMasterSortColumn = column;
            this.chemicalMasterSortOrder = 'asc';
        }
        this.currentPage = 1;
        this.doPaginate(1);
    }

    getChemicalKey = (c: { id: number; warehouseId?: string | null }): string => `${c.id}_${c.warehouseId ?? null}`;

    get pesticidePlanProducer() : boolean {
        return this.$store.getters.loginUser.pesticidePlanProducer;
    }

    get isPesticidePlan() : boolean {
        return this.$store.getters.loginUser.isPesticidePlan;
    }
    
    get isVegepaletteUser() : boolean {
        return !this.$store.getters.loginUser.pesticidePlanProducer && !this.$store.getters.loginUser.isPesticidePlan;
    }

    toggleChemicalSelection(c: Chemical) {
        this.checkedChemicals.push(c);
        const key = this.getChemicalKey(c);
        if (this.selectedChemicalKeys.has(key)) {
            this.selectedChemicalKeys.delete(key);
        } else {
            this.selectedChemicalKeys.add(key);
        }
    }

    resetModal () {
        this.searchRecommendation = '';
        this.searchHistory = '';
        this.searchWarehouseMaster = '';
        this.searchChemicalMaster = '';
        this.searchType = 'name';
        this.selectedTab = this.isWorkRecord ? 'warehouse-master' : 'recommendation';
        this.showAllChemicals = false;
        this.recommendations = [];
        this.filteredRecommendations = [];
        this.histories = [];
        this.filteredHistories = [];
        this.chemicalsFromWarehouseMaster = [];
        this.filteredChemicalsFromWarehouseMaster = [];
        this.chemicals = { chemicals: [], hasMore: false, totalCount: 0 };
        this.offset = 0;
        this.totalPages = 0;
        this.searchRecommendation = '';
        this.searchHistory = '';
        this.searchWarehouseMaster = '';
        this.searchChemicalMaster = '';
        this.warehouses = [];
        this.selectedWarehouseId = null;
        this.currentPage = 1;
        this.isSpinnerLoading = false;
        this.isChemicalMasterLoading = false;
        this.chemicalMasterSortColumn = 'id';
        this.chemicalMasterSortOrder = 'asc';
        this.sortOrders = {
            recommendation: true,
            history: true,
            'warehouse-master': true
        };
        this.sortFields = createChemicalSortFields();
    }

    @Watch('showAllChemicals')
    onShowAllChemicalsChanged(newVal: boolean) {
        this.currentPage = 1;
        this.doPaginate(1);
    }

    @Watch('isShowPesticideSelectionModal')
    onModalOpen(val: boolean) {
        this.resetModal();
        if (val) {
            this.selectedChemicalKeys = new Set(
                this.initialValue.map(c => `${c.id}_${c.warehouseId}`)
            );
            this.getChemicalList();
            this.doPaginate(1);
        }
    }

    doPaginate(pageNo: number) {
        this.currentPage = pageNo;
        this.offset = (pageNo - 1) * this.limit
        this.getMasterChemicals();
    }

    @Emit('save')
    save() {
        this.$emit('update:isShowPesticideSelectionModal', false);
        const seen = new Set();

        const chemicalsFromMaster = (this.checkedChemicals || [])
            .filter(c => {
                const key = this.getChemicalKey(c);
                return this.selectedChemicalKeys.has(key);
            })
            .map(c => ({
                id: c.id,
                name: c.displayName ? c.displayName : c.name,
                unitName: null,
                warehouseId: null,
                warehouseName: null,
                lastUsed: null,
                isExpired: c.isExpired ?? false
            }));

        const existingFromInitial: Chemical[] = (this.initialValue || []).map(iv => ({
            id: iv.id,
            name: '',
            unitName: null,
            warehouseId: iv.warehouseId ?? null,
            warehouseName: null,
            lastUsed: null
        }));
    
        this.selectedChemicals = [
            ...this.recommendations,
            ...this.histories,
            ...this.chemicalsFromWarehouseMaster,
            ...chemicalsFromMaster,
            ...existingFromInitial
        ].filter(c => {
            const key = this.getChemicalKey(c);
            return this.selectedChemicalKeys.has(key) && !seen.has(key) && seen.add(key);
        });

        return [...this.selectedChemicals];
    }

    cancel() {
        this.$emit('update:isShowPesticideSelectionModal', false);
        this.showAllChemicals = false;
    }

    async getMasterChemicals() {
        const query = [
            `search_by_${this.searchType}=${this.searchChemicalMaster}`,
            `offset=${this.offset}`,
            `limit=${this.limit}`,
            `sort_column=${this.chemicalMasterSortColumn}`,
            `sort_order=${this.chemicalMasterSortOrder}`,
        ].join('&');
        const cropItemIds = Array.isArray(this.cropItemId) ? this.cropItemId : [this.cropItemId];
        const cropItemId = cropItemIds[cropItemIds.length - 1];
        const url = this.showAllChemicals || !cropItemId
            ? `/admin/chemicals?${query}`
            : `/admin/chemicals/${cropItemId}?${query}`;
        this.isChemicalMasterLoading = true;
        try {
            const res = await this.$apiClient.doRequest('get', url) as ChemicalsResponse;
            this.chemicals = toCamel(res) as Chemicals;
            this.totalPages = Math.ceil(this.chemicals.totalCount / this.limit);
        } catch (error) {
            this.chemicals = { chemicals: [], hasMore: false, totalCount: 0 };
            this.$toasted.error('データの取得に失敗しました。');
        } finally {
            this.isChemicalMasterLoading = false;
        }
    }

    async getChemicalList() {
        if (!this.isWorkRecord) {
            if (!this.pdTenantId || !this.sitemId) return;
        }
        try {
            this.isSpinnerLoading = true;
            const sitemId = this.sitemId ? this.sitemId : null;
            const camelCasedResponse = toCamel(await this.$apiClient.doRequest('get', `/admin/chemicals/${this.pdTenantId}/${sitemId}?is_cultivation_history=${!this.isWorkRecord}`)) as ChemicalsGet;
            this.recommendations = camelCasedResponse.recommendations;
            this.filteredRecommendations = camelCasedResponse.recommendations;
            this.histories = camelCasedResponse.histories;
            this.filteredHistories = camelCasedResponse.histories;
            this.chemicalsFromWarehouseMaster = camelCasedResponse.chemicalsFromWarehouseMaster;
            this.filteredChemicalsFromWarehouseMaster = [];
            this.applySort('recommendation');
            this.applySort('history');
            this.warehouses = [...new Map(
                camelCasedResponse.chemicalsFromWarehouseMaster
                    .filter(f => f.warehouseId !== null && f.warehouseName !== null)
                    .map(f => [f.warehouseId as string, { warehouseId: f.warehouseId as string, warehouseName: f.warehouseName as string }])
            ).values()];
        } catch (error) {
            this.$toasted.error('データの取得に失敗しました。');
        } finally {
            this.isSpinnerLoading = false;
        }
    }

    @Watch('searchRecommendation')
    doSearchRecommendation() {
        if (this.searchRecommendation) {
            const keyword = this.searchRecommendation;
            this.filteredRecommendations = this.recommendations.filter(c =>
                c.name.includes(keyword)
            );
        } else {
            this.filteredRecommendations = this.recommendations;
        }
        this.applySort('recommendation');
    }

    @Watch('searchHistory')
    doSearchHistory() {
        if (this.searchHistory) {
            const keyword = this.searchHistory;
            this.filteredHistories = this.histories.filter(c =>
                c.name.includes(keyword)
            );
        } else {
            this.filteredHistories = this.histories;
        }
        this.applySort('history');
    }

    @Watch('searchWarehouseMaster')
    doSearchWarehouseMaster() {
        if (this.searchWarehouseMaster) {
            const keyword = this.searchWarehouseMaster;
            this.filteredChemicalsFromWarehouseMaster = this.chemicalsFromWarehouseMaster.filter(c =>
                c.name.includes(keyword) &&
                c.warehouseId === this.selectedWarehouseId
            );
        } else {
            this.filteredChemicalsFromWarehouseMaster = this.chemicalsFromWarehouseMaster.filter(c =>
                c.warehouseId === this.selectedWarehouseId
            );
        }
        this.applySort('warehouse-master');
    }

    @Watch('selectedWarehouseId')
    filterWarehouseChemicals() {
        if (this.selectedWarehouseId) {
            this.searchWarehouseMaster = '';
            this.filteredChemicalsFromWarehouseMaster = this.chemicalsFromWarehouseMaster.filter(chemical =>
                chemical.warehouseId === this.selectedWarehouseId
            );
            this.applySort('warehouse-master');
        } else {
            this.filteredChemicalsFromWarehouseMaster = []
        }
    }
}
